
import { buildGroupHierarchy, formatCurrency, GetGroupForFilter, systemCurrencies } from "../../../../Utils/Utils";
import { Guid } from "js-guid";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useApi from "../../../../Utils/BackendClient";
import { UserPermissionContext } from "../../../../context/UserPermissionProvider";
import { useIsMobile } from "../../../../Utils/ResizeUtils";

import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import { TreeSelect } from "primereact/treeselect";

import EnumsContext from "../../../../context/enums-context";
import useGetEnums from "../../../../Utils/EnumsUtils";


const useWidget = (fetchFunction, initialDates, toast, errorMessage, globalDates, currency, productGroup, division) => {
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dates, setDates] = useState(initialDates);

  useEffect(() => {
    const activeDates = globalDates || dates;
    if (activeDates[0] !== null) {
      const fetchData = async () => {
        try {
          const response = await fetchFunction(activeDates, currency, GetGroupForFilter(productGroup), division);
          if (response.status === 200) {
            setData(response.data);
            setResult(calculateResult(response.data));
            setLoaded(true);
          } else {
            throw new Error(response.status === 403 ? "Access denied" : `Error ${response.status}`);
          }
        } catch (error) {
          toast.current.show({
            severity: "error",
            summary: errorMessage,
            detail: error.message,
            life: 3000,
          });
          setData([]);
          setResult(calculateResult(null));
        }
      };
      fetchData();
    }
  }, [fetchFunction, dates, globalDates, toast, errorMessage, currency, productGroup, division]);

  return { data, result, loaded, dates, setDates };
};

const calculateResult = (widgetData) => {
  let result = 0;
  if (widgetData !== null) {
    if (widgetData.previous_value !== 0) {
      result = Math.round((widgetData.current_value / widgetData.previous_value) * 100 - 100);
    } else if (widgetData.current_value !== 0) {
      result = 100;
    }
  }
  const style = result === 0 ? "" : result > 0 ? "text-green-500" : "text-red-500";
  return { result, style };
};

const WidgetCard = ({ data, result, loaded, dates, setDates, icon, color, currency, isDark }) => (
  <>
    {!loaded ? (
      <div className="card flex justify-content-center">
        <Skeleton height="200px" />
      </div>
    ) : (
      <Card className="mb-0" style={{ height: "200px" }}>
        <>
          <div className="flex justify-content-between mb-2">
            <div>
              <span className="block text-500 font-medium mb-3">{data?.name}</span>
              <div className="text-900 font-medium text-2xl">
                {currency
                  ? formatCurrency(data?.current_value, currency)
                  : data?.current_value}
              </div>
            </div>
            <div
              className={`flex align-items-center justify-content-center bg-${color}-100 border-round`}
              style={{ width: "3rem", height: "3rem" }}
            >
              <i className={`pi ${icon} text-${color}-500 text-xl`} />
            </div>
          </div>
          <span className="font-medium">в період </span>
          <Calendar
            dateFormat="dd/mm/yy"
            value={dates}
            onChange={(e) => setDates(e.value)}
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            inputStyle={isDark ? { border: "none", paddingLeft: "0", background: "#1f2937" } : { border: "none", paddingLeft: "0" }}
          />
          <div>
            <span className={`${result?.style} font-medium`}>{result?.result}% </span>
            <span className="text-500">порівняно з аналогічним періодом до цього</span>
          </div>
        </>
      </Card>)}
  </>
);

const DashBoards = ({ isDarkTheme }) => {

  const isMobile = useIsMobile();
  const { fetchCostsWidget, fetchPayableWidget, fetchProductCountWidget, fetchNetProfitWidget, fetchProductsExtended, fetchReceivableWidget, fetchRevenueWidget, fetchServicesGivenWidget, fetchTopProductsWidget } = useApi();
  const permCtx = useContext(UserPermissionContext);
  const ctx = useContext(EnumsContext);
  const toast = useRef(null);

  const enumTypes = useMemo(() => ["divisions"], []);

  useGetEnums(enumTypes);

  const initialDates = (() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return [sevenDaysAgo, today];
  })();

  const [globalDates, setGlobalDates] = useState(initialDates);
  const [globalCurrency, setGlobalCurrency] = useState("USD");
  const [receivables, setReceivables] = useState(null);
  const [receivablesLoaded, setReceivablesLoaded] = useState(false);
  const [payables, setPayables] = useState(null);
  const [payablesLoaded, setPayablesLoaded] = useState(false);
  const [netProfit, setNetProfit] = useState(null);
  const [netProfitLoaded, setNetProfitLoaded] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [topProductsLoaded, setTopProductsLoaded] = useState(false);
  const [productGroup, setProductGroup] = useState(null);
  const [productGroups, setProductGroups] = useState([]);
  const [division, setDivision] = useState(null);

  const formatGlobalDates = () => {
    if (globalDates === null || globalDates[1] === null) {
      return null
    }

    return globalDates;
  }

  useEffect(() => {

    const loadProductGroups = async () => {
      const response = await fetchProductsExtended();

      if (response.status === 200) {
        const groups = buildGroupHierarchy(response.data);
        setProductGroups(groups);
      }
    }

    loadProductGroups();
  }, [fetchProductsExtended]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchReceivableWidget(globalCurrency, division);
        if (response.status === 200) {
          setReceivables(response.data);
          setReceivablesLoaded(true);
        } else {
          throw new Error(response.status === 403 ? "Access denied" : `Error ${response.status}`);
        }
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Помилка завантаження віджету дебіторської заборгованості",
          detail: error.message,
          life: 3000,
        });
      }
    };
    fetchData();
  }, [division, fetchReceivableWidget, globalCurrency]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchPayableWidget(globalCurrency, division);
        if (response.status === 200) {
          setPayables(response.data);
          setPayablesLoaded(true);
        } else {
          throw new Error(response.status === 403 ? "Access denied" : `Error ${response.status}`);
        }
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Помилка завантаження віджету дебіторської заборгованості",
          detail: error.message,
          life: 3000,
        });
      }
    };
    fetchData();
  }, [division, fetchPayableWidget, globalCurrency]);

  useEffect(() => {
    const fetchData = async () => {

      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);

      const dates = (globalDates !== null && globalDates[1] !== null) ? globalDates : [sevenDaysAgo, today];

      try {
        const response = await fetchNetProfitWidget(dates, globalCurrency, GetGroupForFilter(productGroup), division);
        if (response.status === 200) {
          setNetProfit(response.data);
          setNetProfitLoaded(true);
        } else {
          throw new Error(response.status === 403 ? "Access denied" : `Error ${response.status}`);
        }
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Помилка завантаження віджету чистого прибутку",
          detail: error.message,
          life: 3000,
        });
      }
    };
    fetchData();
  }, [division, fetchNetProfitWidget, globalCurrency, globalDates, productGroup]);

  useEffect(() => {
    const fetchData = async () => {

      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);

      const dates = (globalDates !== null && globalDates[1] !== null) ? globalDates : [sevenDaysAgo, today];

      try {
        const response = await fetchTopProductsWidget(dates, globalCurrency, GetGroupForFilter(productGroup), division);
        if (response.status === 200) {
          setTopProducts(response?.data.results);
          setTopProductsLoaded(true);
        } else {
          setTopProducts([]);
          throw new Error(response.status === 403 ? "Access denied" : `Error ${response.status}`);
        }
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Помилка завантаження віджету чистого прибутку",
          detail: error.message,
          life: 3000,
        });
      }
    };
    fetchData();
  }, [division, fetchTopProductsWidget, globalDates, globalCurrency, productGroup]);

  const expenses = useWidget(fetchCostsWidget, initialDates, toast, "Помилка віджету витрат", formatGlobalDates(), globalCurrency, productGroup, division);
  const products = useWidget(fetchProductCountWidget, initialDates, toast, "Помилка віджету кількості проданих товарів", formatGlobalDates(), globalCurrency, productGroup, division);
  const services = useWidget(fetchServicesGivenWidget, initialDates, toast, "Помилка віджету кількості наданих послуг", formatGlobalDates(), globalCurrency, productGroup, division);
  const revenue = useWidget(fetchRevenueWidget, initialDates, toast, "Помилка віджету доходу", formatGlobalDates(), globalCurrency, productGroup, division);

  const synchronizeDates = (newDates) => {
    setGlobalDates(newDates);
    if (newDates[1] !== null) {
      expenses.setDates(newDates);
      products.setDates(newDates);
      services.setDates(newDates);
      revenue.setDates(newDates);
    }
  };

  const Filters = () => {
    const CalendarFilter = (
      <FloatLabel>
        <Calendar
          dateFormat="dd/mm/yy"
          value={globalDates}
          onChange={(e) => synchronizeDates(e.value)}
          selectionMode="range"
          className="w-full"
          readOnlyInput
          hideOnRangeSelection
        />
        <label>Період</label>
      </FloatLabel>
    );

    const CurrencyFilter = (
      <FloatLabel>
        <Dropdown
          value={globalCurrency}
          onChange={(e) => setGlobalCurrency(e.value)}
          options={systemCurrencies}
          className="w-full"
        />
        <label>Валюта</label>
      </FloatLabel>
    );

    const ProductGroupFilter = (
      <FloatLabel>
        <TreeSelect
          name="set_group"
          value={productGroup}
          onChange={(e) => setProductGroup(e.value)}
          options={productGroups}
          showClear
          className="w-full"
          style={{ minWidth: '15rem' }}
        />
        <label>Група товарів</label>
      </FloatLabel>
    );

    const DivisionFilter = (
      <FloatLabel>
        <Dropdown
          value={division}
          onChange={(e) => setDivision(e.value)}
          options={ctx.enums.divisions?.data}
          showClear
          className="w-full"
          style={{ minWidth: '15rem' }}
        />
        <label>Точка продажу</label>
      </FloatLabel>
    );

    if (isMobile) {
      return (
        <>
          <div className="col-8">{CalendarFilter}</div>
          <div className="col-4">{CurrencyFilter}</div>
          <div className="col-12">{DivisionFilter}</div>
          <div className="col-12">{ProductGroupFilter}</div>
        </>
      );
    }

    return (
      <div className="flex flex-row gap-2">
        {CalendarFilter}
        {CurrencyFilter}
        {DivisionFilter}
        {ProductGroupFilter}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      {permCtx.permissions?.includes("dashboards.view_dashboard") && (<><div>
        <Card className="mb-3">
          <div className="grid">
            {Filters()}
          </div>
        </Card>
      </div>
        <div className="grid">
          <div className="col-12 lg:col-6 xl:col-3" key="expenses">
            <WidgetCard {...expenses} icon="pi-shopping-cart" color="blue" currency={globalCurrency} isDark={isDarkTheme} />
          </div>
          <div className="col-12 lg:col-6 xl:col-3" key="products">
            <WidgetCard {...products} icon="pi-phone" color="orange" isDark={isDarkTheme} />
          </div>
          <div className="col-12 lg:col-6 xl:col-3" key="services">
            <WidgetCard {...services} icon="pi-wrench" color="cyan" isDark={isDarkTheme} />
          </div>
          <div className="col-12 lg:col-6 xl:col-3" key="revenue">
            <WidgetCard {...revenue} icon="pi-money-bill" color="green" currency={globalCurrency} isDark={isDarkTheme} />
          </div>
          <div className="col-12 lg:col-6">
            {!receivablesLoaded ? (
              <div className="card flex justify-content-center">
                <Skeleton height="348px" />
              </div>
            ) : (
              <Card className="mb-0" style={{ minHeight: "348px" }}>
                <span className="block text-500 font-medium mb-3">{receivables?.name}</span>
                <div className="flex justify-content-between border-bottom-1 surface-border p-3">
                  <span className="font-semibold text-color-secondary text-xl">Загальна сума</span>
                  <span className="text-green-600 text-2xl font-bold">{formatCurrency(receivables?.sum, globalCurrency)}</span>
                </div>
                <div className="p-3">
                  {receivables?.top_cagents.length === 0 && <span className="text-color-secondary"> Немає дебіторської заборгованості</span>}
                  {receivables?.top_cagents.map(cagent =>
                    <div className="flex align-items-center justify-content-between py-2" key={Guid.newGuid()}>
                      <div className="flex gap-3 text-lg align-items-center">
                        <i className="pi pi-user text-color-secondary" />
                        <span className="text-color-secondary">{cagent?.cagent_name ?? "(Невідомий клієнт)"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-base">{formatCurrency(cagent?.sum, globalCurrency)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>)}
          </div>
          <div className="col-12 lg:col-6">
            {!payablesLoaded ? (
              <div className="card flex justify-content-center">
                <Skeleton height="348px" />
              </div>
            ) : (
              <Card className="mb-0" style={{ minHeight: "348px" }}>
                <span className="block text-500 font-medium mb-3">{payables?.name}</span>
                <div className="flex justify-content-between border-bottom-1 surface-border p-3">
                  <span className="font-semibold text-color-secondary text-xl">Загальна сума</span>
                  <span className="text-red-600 text-2xl font-bold">{formatCurrency(payables?.sum, globalCurrency)}</span>
                </div>
                <div className="p-3">
                  {payables?.top_cagents.length === 0 && <span className="text-color-secondary"> Немає кредиторської заборгованості</span>}
                  {payables?.top_cagents.map(cagent =>
                    <div key={Guid.newGuid()} className="flex align-items-center justify-content-between py-2">
                      <div className="flex gap-3 text-lg align-items-center">
                        <i className="pi pi-user text-color-secondary" />
                        <span className="text-color-secondary">{cagent?.cagent_name ?? "(Невідомий клієнт)"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-base">{formatCurrency(cagent?.sum, globalCurrency)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>)}
          </div>
          <div className="col-12 lg:col-6">
            {!topProductsLoaded ? (
              <div className="card flex justify-content-center">
                <Skeleton height="367px" borderRadius="16px" />
              </div>
            ) : (
              <Card className="mb-0" style={{ minHeight: "367px" }}>
                <div className="flex justify-content-between">
                  <span className="block text-500 font-medium mb-3">Найпопулярніші товари</span>
                </div>
                <div className="p-3">
                  <DataTable value={Object.values(topProducts)} emptyMessage="Немає товарів">
                    <Column field="product_name" header="Назва" align="right" style={{ width: '30%' }} />
                    <Column field="quantity" header="Кількість" align="right" style={{ width: '30%' }} />
                    <Column field="revenue" header="Дохід" align="right" body={rowdata => formatCurrency(rowdata.revenue, globalCurrency)} style={{ width: '30%' }} />
                    <Column field="net_profit" header="Чистий прибуток" align="right" body={rowdata => formatCurrency(rowdata.net_profit, globalCurrency)} style={{ width: '30%' }} />
                  </DataTable>
                </div>
              </Card>)}
          </div>
          <div className="col-12 lg:col-6">
            {!netProfitLoaded ? (
              <Skeleton height="367px" borderRadius="16px" />
            ) : (
              <Card className="mb-0" style={{ minHeight: "367px" }}>
                <span className="block text-500 font-medium mb-3">{netProfit?.name}</span>
                <div className="flex justify-content-between border-bottom-1 surface-border p-3">
                  <span className="font-semibold text-color-secondary text-xl">Загальна сума</span>
                  <span className="text-green-600 text-2xl font-bold">{formatCurrency(netProfit?.net_profit, globalCurrency)}</span>
                </div>
                <div className="p-3">
                  {netProfit?.net_profit_in_currencies?.length === 0 && <span className="text-color-secondary"> Немає чистого прибутку</span>}
                  <DataTable value={Object.values(netProfit.net_profit_in_currencies)}>
                    <Column field="currency" header="Валюта" style={{ width: '10%' }} />
                    <Column field="profit" header="Дохід" body={rowdata => formatCurrency(rowdata.profit, rowdata.currency)} align="right" style={{ width: '30%' }} />
                    <Column field="cost" header="Витрати" body={rowdata => formatCurrency(rowdata.cost, rowdata.currency)} align="right" style={{ width: '30%' }} />
                    <Column field="net_profit" header="Прибуток" body={rowdata => formatCurrency(rowdata.net_profit, rowdata.currency)} align="right" style={{ width: '30%' }} />
                  </DataTable>
                </div>
              </Card>)}
          </div>
        </div>
      </>)}
    </>
  );
};

export default DashBoards;